import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { Plus, Edit, Trash2, Tag, X as XIcon, Camera, Search, Star } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";

const emptyProduct = { name: "", sku: "", brand_id: "", category: "camera", subcategory: "", price: 0, stock: 0, description: "", specifications: {}, features: [], tech_stack: "", user_manual_url: "", images: [], is_active: true, is_featured: false };

async function fileToBase64(file, max = 1200) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const img = new Image();
      img.onload = () => {
        const c = document.createElement("canvas");
        let { width, height } = img;
        if (width > max || height > max) { const s = Math.min(max / width, max / height); width = Math.floor(width * s); height = Math.floor(height * s); }
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", 0.82));
      };
      img.onerror = reject; img.src = r.result;
    };
    r.onerror = reject; r.readAsDataURL(file);
  });
}

export default function ProductsTab() {
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [brands, setBrands] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState("all");
  const [brand, setBrand] = useState("all");
  const [open, setOpen] = useState(null);
  const [brandOpen, setBrandOpen] = useState(null);

  const load = async () => {
    const [p, b] = await Promise.all([
      api.get("/products", { params: { q: q || undefined, category: category !== "all" ? category : undefined, brand_id: brand !== "all" ? brand : undefined } }),
      api.get("/brands"),
    ]);
    setItems(p.data); setBrands(b.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, category, brand]);

  const save = async () => {
    const p = open.product;
    const bn = brands.find(b => b.brand_id === p.brand_id)?.name || "";
    const payload = { ...p, brand_name: bn, price: parseFloat(p.price || 0), stock: parseInt(p.stock || 0), features: Array.isArray(p.features) ? p.features : String(p.features || "").split("\n").filter(x => x.trim()) };
    try {
      if (open.mode === "new") await api.post("/products", payload);
      else await api.put(`/products/${p.product_id}`, payload);
      toast.success("Saved"); setOpen(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this product?")) return; await api.delete(`/products/${id}`); toast.success("Deleted"); load(); };

  const saveBrand = async () => {
    try {
      if (brandOpen.mode === "new") await api.post("/brands", brandOpen.brand);
      else await api.put(`/brands/${brandOpen.brand.brand_id}`, brandOpen.brand);
      toast.success("Brand saved"); setBrandOpen(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const onImage = async (file, product) => {
    const b64 = await fileToBase64(file);
    setOpen({ ...open, product: { ...product, images: [...(product.images || []), b64] } });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Products & Inventory</h1>
          <p className="text-sm text-muted-foreground mt-1">{items.length} products · {brands.length} brands</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setBrandOpen({ mode: "new", brand: { name: "", description: "", logo_url: "", website: "" } })} data-testid="add-brand-btn"><Tag className="h-4 w-4 mr-2" />New Brand</Button>
          <Button onClick={() => setOpen({ mode: "new", product: { ...emptyProduct } })} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" data-testid="add-product-btn"><Plus className="h-4 w-4 mr-2" />New Product</Button>
        </div>
      </div>
      <Card className="p-3 flex flex-col md:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search products…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-10" data-testid="products-search" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>{["all", "camera", "alarm", "intercom", "dvr", "accessory"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={brand} onValueChange={setBrand}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All brands</SelectItem>{brands.map(b => <SelectItem key={b.brand_id} value={b.brand_id}>{b.name}</SelectItem>)}</SelectContent>
        </Select>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {items.map(p => (
          <Card key={p.product_id} className="p-4">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-16 h-16 bg-secondary rounded-lg overflow-hidden shrink-0 flex items-center justify-center">
                {p.images?.[0] ? <img src={p.images[0]} alt="" className="w-full h-full object-cover" /> : <Camera className="h-6 w-6 text-muted-foreground/40" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <div className="text-xs text-eagle-gold uppercase tracking-wider truncate">{p.brand_name}</div>
                  {p.is_featured && <Star className="h-3 w-3 fill-eagle-gold text-eagle-gold" />}
                </div>
                <div className="font-medium truncate text-sm">{p.name}</div>
                <div className="text-xs text-muted-foreground">${(p.price || 0).toLocaleString()} · {p.stock} left</div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1" onClick={() => setOpen({ mode: "edit", product: { ...p, features: (p.features || []).join("\n") } })} data-testid={`edit-product-${p.product_id}`}><Edit className="h-3 w-3 mr-1" />Edit</Button>
              <Button size="sm" variant="ghost" onClick={() => del(p.product_id)} data-testid={`delete-product-${p.product_id}`}><Trash2 className="h-3 w-3 text-red-500" /></Button>
            </div>
          </Card>
        ))}
      </div>

      {brands.length > 0 && (
        <Card className="p-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Brands</div>
          <div className="flex flex-wrap gap-2">
            {brands.map(b => (
              <button key={b.brand_id} onClick={() => setBrandOpen({ mode: "edit", brand: b })} className="px-3 py-1.5 rounded-full text-sm bg-secondary hover:bg-eagle-gold hover:text-eagle-ink transition" data-testid={`brand-chip-${b.brand_id}`}>{b.name}</button>
            ))}
          </div>
        </Card>
      )}

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          {open && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl">{open.mode === "new" ? "New Product" : "Edit Product"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2"><Label>Name</Label><Input value={open.product.name} onChange={(e) => setOpen({ ...open, product: { ...open.product, name: e.target.value } })} data-testid="prod-name" /></div>
                <div><Label>SKU / Model</Label><Input value={open.product.sku} onChange={(e) => setOpen({ ...open, product: { ...open.product, sku: e.target.value } })} data-testid="prod-sku" /></div>
                <div>
                  <Label>Brand</Label>
                  <Select value={open.product.brand_id || "__none__"} onValueChange={(v) => setOpen({ ...open, product: { ...open.product, brand_id: v === "__none__" ? "" : v } })}>
                    <SelectTrigger data-testid="prod-brand"><SelectValue placeholder="Choose brand" /></SelectTrigger>
                    <SelectContent><SelectItem value="__none__">— None —</SelectItem>{brands.map(b => <SelectItem key={b.brand_id} value={b.brand_id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={open.product.category} onValueChange={(v) => setOpen({ ...open, product: { ...open.product, category: v } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["camera", "alarm", "intercom", "dvr", "accessory"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Subcategory</Label><Input placeholder="dome / bullet / kit / …" value={open.product.subcategory} onChange={(e) => setOpen({ ...open, product: { ...open.product, subcategory: e.target.value } })} /></div>
                <div><Label>Price (AUD)</Label><Input type="number" value={open.product.price} onChange={(e) => setOpen({ ...open, product: { ...open.product, price: e.target.value } })} data-testid="prod-price" /></div>
                <div><Label>Stock qty</Label><Input type="number" value={open.product.stock} onChange={(e) => setOpen({ ...open, product: { ...open.product, stock: e.target.value } })} data-testid="prod-stock" /></div>
                <div className="md:col-span-2"><Label>Description</Label><Textarea rows={3} value={open.product.description} onChange={(e) => setOpen({ ...open, product: { ...open.product, description: e.target.value } })} data-testid="prod-desc" /></div>
                <div className="md:col-span-2"><Label>Features (one per line)</Label><Textarea rows={3} value={open.product.features || ""} onChange={(e) => setOpen({ ...open, product: { ...open.product, features: e.target.value } })} placeholder={"8 MP resolution\nIR range 30m\nIP67 weatherproof"} /></div>
                <div><Label>Tech stack / compatibility</Label><Input value={open.product.tech_stack} onChange={(e) => setOpen({ ...open, product: { ...open.product, tech_stack: e.target.value } })} /></div>
                <div><Label>User manual URL</Label><Input value={open.product.user_manual_url} onChange={(e) => setOpen({ ...open, product: { ...open.product, user_manual_url: e.target.value } })} /></div>
                <div className="md:col-span-2">
                  <Label>Specifications (JSON)</Label>
                  <Textarea rows={4} value={JSON.stringify(open.product.specifications || {}, null, 2)} onChange={(e) => { try { setOpen({ ...open, product: { ...open.product, specifications: JSON.parse(e.target.value || "{}") } }); } catch { /* ignore */ } }} data-testid="prod-specs" />
                </div>
                <div className="flex items-center gap-3"><Switch checked={open.product.is_featured} onCheckedChange={(v) => setOpen({ ...open, product: { ...open.product, is_featured: v } })} /><Label>Featured</Label></div>
                <div className="flex items-center gap-3"><Switch checked={open.product.is_active} onCheckedChange={(v) => setOpen({ ...open, product: { ...open.product, is_active: v } })} /><Label>Active</Label></div>
                <div className="md:col-span-2">
                  <Label>Images</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(open.product.images || []).map((img, i) => (
                      <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-border group">
                        <img src={img} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => setOpen({ ...open, product: { ...open.product, images: open.product.images.filter((_, j) => j !== i) } })} className="absolute top-1 right-1 h-5 w-5 rounded-full bg-red-600 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center"><XIcon className="h-3 w-3" /></button>
                      </div>
                    ))}
                    <label className="w-20 h-20 rounded-lg border-2 border-dashed border-border hover:border-eagle-gold flex items-center justify-center cursor-pointer">
                      <Plus className="h-6 w-6 text-muted-foreground" />
                      <input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && onImage(e.target.files[0], open.product)} />
                    </label>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(null)}>Cancel</Button>
                <Button onClick={save} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" data-testid="prod-save-btn">Save Product</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!brandOpen} onOpenChange={(o) => !o && setBrandOpen(null)}>
        <DialogContent>
          {brandOpen && (
            <>
              <DialogHeader><DialogTitle>{brandOpen.mode === "new" ? "New Brand" : "Edit Brand"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name</Label><Input value={brandOpen.brand.name} onChange={(e) => setBrandOpen({ ...brandOpen, brand: { ...brandOpen.brand, name: e.target.value } })} data-testid="brand-name-input" /></div>
                <div><Label>Website</Label><Input value={brandOpen.brand.website} onChange={(e) => setBrandOpen({ ...brandOpen, brand: { ...brandOpen.brand, website: e.target.value } })} /></div>
                <div><Label>Logo URL</Label><Input value={brandOpen.brand.logo_url} onChange={(e) => setBrandOpen({ ...brandOpen, brand: { ...brandOpen.brand, logo_url: e.target.value } })} /></div>
                <div><Label>Description</Label><Textarea rows={3} value={brandOpen.brand.description} onChange={(e) => setBrandOpen({ ...brandOpen, brand: { ...brandOpen.brand, description: e.target.value } })} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBrandOpen(null)}>Cancel</Button>
                {brandOpen.mode === "edit" && <Button variant="ghost" onClick={async () => { await api.delete(`/brands/${brandOpen.brand.brand_id}`); toast.success("Brand deleted"); setBrandOpen(null); load(); }}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                <Button onClick={saveBrand} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover">Save</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}